import cx from 'classnames';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Icon } from '@components/icon/Icon';
import typography from '~styles/typography.scss';
import { AssignServerModeFloorPlan } from '../../floorPlan/AssignServerModeFloorPlan';
import { useServiceSearchParams } from '../../hooks/useServiceSearchParams';
import { ServiceFloorPlanSelector } from '../../ServiceFloorPlanSelector';
import { useReservationServiceContext } from '../../state/ReservationServiceContext';
import { ControlledColorPickerRadio } from './ControlledColorPickerRadio';
import styles from './ServerDrawer.scss';
import {
  SERVER_BADGE_COLORS,
  type ServerFormData,
  useServerFormContext,
} from './ServerFormContext';
import { ServersOptionsMenu } from './ServersOptionsMenu';

export const ServerDrawer = () => {
  const serviceSearchParams = useServiceSearchParams();
  const { selectedFloorPlan } = useReservationServiceContext();
  const {
    form: { control, handleSubmit, reset },
    handleCreateServer,
    handleUpdateServer,
    selectedServer,
    setSelectedServer,
  } = useServerFormContext();

  const closeAndResetForm = () => {
    setSelectedServer(null);
    serviceSearchParams.clearAll();
    reset();
  };

  const isEditMode = !!selectedServer;

  return (
    <section data-testid="server-drawer" className={styles.container}>
      <form>
        <div className={styles.headerBar}>
          <h2 className={typography.h4}>
            <Icon className={styles.serverIcon} name="personOutline" />
            {isEditMode ? 'Edit Server' : 'Create Server'}
          </h2>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              onClick={closeAndResetForm}
              variant={ButtonVariants.Tertiary}
            />
            {isEditMode ? (
              <Button
                label="Save Edits"
                onClick={handleSubmit(async (data: ServerFormData) => {
                  await handleUpdateServer(data, closeAndResetForm);
                })}
                variant={ButtonVariants.Primary}
              />
            ) : (
              <Button
                label="Create Server"
                onClick={handleSubmit(async (data: ServerFormData) => {
                  await handleCreateServer(data, closeAndResetForm);
                })}
                variant={ButtonVariants.Primary}
              />
            )}
            {selectedServer && <ServersOptionsMenu />}
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.gridWithEquisizedColumns}>
            <ControlledFormInput
              className={styles.serverNameInput}
              control={control}
              rules={{
                required: true,
                minLength: {
                  message: 'Server Name must be at least 2 characters',
                  value: 2,
                },
                maxLength: {
                  message: 'Server Name must be 50 characters or less',
                  value: 50,
                },
              }}
              label="Server Name"
              name="name"
              type="text"
              variant={LayoutVariant.Horizontal}
            />
            <ControlledColorPickerRadio
              colors={SERVER_BADGE_COLORS.map((color) => color.backgroundColor)}
              control={control}
            />
          </div>
          <div className={styles.floorPlanControls}>
            <ServiceFloorPlanSelector />
          </div>
          {selectedFloorPlan && (
            <AssignServerModeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              tables={selectedFloorPlan.floorPlanTables}
            />
          )}
        </div>
      </form>
    </section>
  );
};
